// src/store/ictBrochureSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import apiClient from "./apiClient";

const ENDPOINT = "/ict-brochures/";

// Helper: build FormData exactly as backend expects
// backend expects: ict_id (Form), status (Form), brochure (File)
const buildPayload = ({ ictId, status = "A", brochureFile }) => {
  const fd = new FormData();

  // IMPORTANT: Must be ict_id
  fd.append("ict_id", String(Number(ictId)));

  // status is Form
  fd.append("status", status);

  // IMPORTANT: Must be brochure (UploadFile)
  if (brochureFile instanceof File) {
    fd.append("brochure", brochureFile);
  }

  return fd;
};

// ✅ FETCH by ICT: GET /ictData/{ict_id}/brochures
export const fetchBrochuresByIctId = createAsyncThunk(
  "ictBrochure/fetchByIctId",
  async (ictId, { rejectWithValue }) => {
    try {
      const res = await apiClient.get(`/ictData/${ictId}/brochures`);
      return res.data || [];
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

// ✅ INSERT: POST /ict-brochures/ (multipart)
export const insertBrochure = createAsyncThunk(
  "ictBrochure/insert",
  async ({ ictId, brochureFile, status = "A" }, { rejectWithValue }) => {
    try {
      if (!ictId) return rejectWithValue("Missing ictId (from URL)");
      if (!(brochureFile instanceof File)) return rejectWithValue("Missing brochure file");

      const body = buildPayload({ ictId, status, brochureFile });

      const res = await apiClient.post(ENDPOINT, body, {
        headers: { "Content-Type": "multipart/form-data" }, // ✅ same as trainers
      });

      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

// ✅ UPDATE: PUT /ict-brochures/{brochure_id} (multipart)
export const updateBrochure = createAsyncThunk(
  "ictBrochure/update",
  async ({ id, status, brochureFile }, { rejectWithValue }) => {
    try {
      if (!id) return rejectWithValue("Missing brochure id");

      const fd = new FormData();
      if (status) fd.append("status", status);
      if (brochureFile instanceof File) fd.append("brochure", brochureFile);

      const res = await apiClient.put(`${ENDPOINT}${id}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

// ✅ PATCH STATUS: /ict-brochures/{id}/status (json)
export const patchBrochureStatus = createAsyncThunk(
  "ictBrochure/patchStatus",
  async ({ id, status }, { rejectWithValue }) => {
    try {
      const res = await apiClient.patch(`${ENDPOINT}${id}/status`, { status });
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

// ✅ DELETE: /ict-brochures/{id}
export const deleteBrochure = createAsyncThunk(
  "ictBrochure/delete",
  async (id, { rejectWithValue }) => {
    try {
      const res = await apiClient.delete(`${ENDPOINT}${id}`);
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

const ictBrochureSlice = createSlice({
  name: "ictBrochure",
  initialState: {
    brochures: [],
    loading: false,
    error: null,
  },
  reducers: {
    resetBrochures: (state) => {
      state.brochures = [];
      state.loading = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // fetch
      .addCase(fetchBrochuresByIctId.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBrochuresByIctId.fulfilled, (state, action) => {
        state.loading = false;
        state.brochures = action.payload || [];
      })
      .addCase(fetchBrochuresByIctId.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // insert
      .addCase(insertBrochure.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(insertBrochure.fulfilled, (state, action) => {
        state.loading = false;
        state.brochures.unshift(action.payload);
      })
      .addCase(insertBrochure.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // update
      .addCase(updateBrochure.fulfilled, (state, action) => {
        const idx = state.brochures.findIndex((b) => b.id === action.payload?.id);
        if (idx !== -1) state.brochures[idx] = action.payload;
      })
      .addCase(updateBrochure.rejected, (state, action) => {
        state.error = action.payload;
      })

      // patch
      .addCase(patchBrochureStatus.fulfilled, (state, action) => {
        const idx = state.brochures.findIndex((b) => b.id === action.payload?.id);
        if (idx !== -1) state.brochures[idx] = action.payload;
      })
      .addCase(patchBrochureStatus.rejected, (state, action) => {
        state.error = action.payload;
      })

      // delete
      .addCase(deleteBrochure.fulfilled, (state, action) => {
        const deletedId = action.payload?.id ?? action.meta.arg;
        state.brochures = state.brochures.filter((b) => b.id !== deletedId);
      })
      .addCase(deleteBrochure.rejected, (state, action) => {
        state.error = action.payload;
      });
  },
});

export const { resetBrochures } = ictBrochureSlice.actions;
export default ictBrochureSlice.reducer;
